import type { FormInstance } from 'antd';
import { Button, Form, Popover } from 'antd';
import { useState } from 'react';

import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import type { FieldActiveRules } from './PricingRuleModal';
import { PricingRulePopin } from './PricingRulePopin';

type PricingRuleModalFooterProps = {
  handleCancelModal: () => void;
  handleDeactivationAutomation: () => void;
  isFormInvalid: boolean;
  form: FormInstance;
  hasValue: boolean;
  openPricingRule: boolean;
  openLastMinutePricingRule: boolean;
  isSingleRate: boolean;
  activeRules: FieldActiveRules;
  isUpdatingPricingRule: boolean;
};

export const PricingRuleModalFooter = ({
  handleCancelModal,
  handleDeactivationAutomation,
  isFormInvalid,
  form,
  hasValue,
  openPricingRule,
  openLastMinutePricingRule,
  isSingleRate,
  activeRules,
  isUpdatingPricingRule,
}: PricingRuleModalFooterProps) => {
  const formValues = Form.useWatch([], form);

  const [isConfirmationPopinVisible, setIsConfirmationPopinVisible] =
    useState(false);
  const [isUpdatePopinVisible, setIsUpdatePopinVisible] = useState(false);

  const showDeactivatePopover =
    hasValue && !openPricingRule && !openLastMinutePricingRule;

  const hasStandardDiscount = activeRules.filter(
    (item) => item.standardDiscount
  ).length;

  const hasLastMinuteStandardDiscount = activeRules.filter(
    (item) => item.lastMinuteDiscount
  ).length;

  const showMultiRateLMRule =
    !isSingleRate &&
    hasLastMinuteStandardDiscount &&
    !hasStandardDiscount &&
    !!formValues?.lastMinuteDiscountValue &&
    !!formValues?.lastMinuteDays;

  const showMultiRateGlobalRule =
    !isSingleRate && hasStandardDiscount && !!formValues?.discount;

  const ClickNoDeactivationPopin = () => {
    amplitudeTrack('Click Cancel popin');
    setIsConfirmationPopinVisible(false);
  };

  const ClickYesDeactivationPopin = () => {
    amplitudeTrack('Deactivate rule');
    setIsConfirmationPopinVisible(false);
  };

  const ActiveUpdateButton = ({
    onClick,
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    isUpdatingPricingRule,
  }: {
    onClick?: () => void;
    isUpdatingPricingRule: boolean;
  }) => {
    return (
      <Button
        id="saveAutomatedDiscountRule"
        key="save"
        size="large"
        type="primary"
        htmlType="submit"
        onClick={onClick}
        disabled={isFormInvalid}
        loading={isUpdatingPricingRule}
      >
        {hasValue ? 'Update' : 'Activate'}
      </Button>
    );
  };

  const getPopinContent = () => {
    if (showMultiRateGlobalRule) {
      return {
        description:
          'The Staycation rate on your Channel Manager will not be taken into consideration anymore.',
        onClickNo: () => {
          setIsUpdatePopinVisible(false);
        },
        onClickYes: () => {
          form.submit();
        },
      };
    }

    if (showMultiRateLMRule) {
      return {
        description: `From ${pluralize(
          formValues?.lastMinuteDays,
          'day',
          'days'
        )} before arrival, the last minute pricing rule will override your connected rates.`,
        onClickNo: () => {
          setIsUpdatePopinVisible(false);
        },
        onClickYes: () => form.submit(),
      };
    }

    if (showDeactivatePopover) {
      return {
        title: 'Do you really want to deactivate?',
        description:
          'Your Staycation rate and discount will return to the amount they had before pricing rule.',
        onClickNo: () => {
          amplitudeTrack('Click Cancel popin');
          setIsUpdatePopinVisible(false);
        },
        onClickYes: () => {
          handleDeactivationAutomation();
          amplitudeTrack('Deactivate rule');
          setIsUpdatePopinVisible(false);
        },
      };
    }

    return {
      title: '',
      description: '',
      onClickNo: () => {
        /* no-op */
      },
      onClickYes: () => {
        /* no-op */
      },
    };
  };

  const { title, description, onClickYes, onClickNo } = getPopinContent();

  return (
    <div className="automatedDiscountModalFooter">
      <div className="automatedDiscountModalFooter__left">
        <Popover
          content={
            <PricingRulePopin
              title="Do you really want to deactivate?"
              description="Your Staycation rate and discount will return to the amount they had
          before pricing rule."
              onClickNo={ClickNoDeactivationPopin}
              onClickYes={() => {
                handleDeactivationAutomation();
                ClickYesDeactivationPopin();
              }}
            />
          }
          placement="top"
          trigger={!!hasValue ? 'click' : []}
          open={hasValue && isConfirmationPopinVisible}
          onOpenChange={(visible) => {
            setIsConfirmationPopinVisible(visible);
          }}
        >
          <Button
            type="link"
            className="automatedDiscountModalFooter__left__remove_rule"
            disabled={!hasValue}
            loading={isUpdatingPricingRule}
          >
            Deactivate
          </Button>
        </Popover>
      </div>
      <div className="automatedDiscountModalFooter__right">
        <Form.Item shouldUpdate>
          {() => (
            <>
              <Button
                id="cancelAutomatedDiscountRule"
                size="large"
                type="ghost"
                onClick={handleCancelModal}
              >
                Cancel
              </Button>
              {showDeactivatePopover ||
              showMultiRateLMRule ||
              showMultiRateGlobalRule ? (
                <Popover
                  content={
                    <PricingRulePopin
                      title={title}
                      description={description}
                      onClickNo={onClickNo}
                      onClickYes={onClickYes}
                      isUpdatingPricingRule={isUpdatingPricingRule}
                    />
                  }
                  placement="top"
                  trigger={'click'}
                  open={isUpdatePopinVisible}
                  onOpenChange={(visible) => {
                    setIsUpdatePopinVisible(visible);
                  }}
                >
                  <ActiveUpdateButton
                    isUpdatingPricingRule={isUpdatingPricingRule}
                  />
                </Popover>
              ) : (
                <ActiveUpdateButton
                  onClick={() => form.submit()}
                  isUpdatingPricingRule={isUpdatingPricingRule}
                />
              )}
            </>
          )}
        </Form.Item>
      </div>
    </div>
  );
};
